import styles from "./PageContainer.module.scss";

import { PropsWithChildren } from "react";

/**
 * Variant options for PageContainer layout behavior.
 * - "constrained": Max-width container with centering (for forms, cards)
 * - "centered": Full-height vertical and horizontal centering
 * - "fullWidth": Full-width layout (for widgets like Calendly)
 */
export type PageContainerVariant = "constrained" | "centered" | "fullWidth";

interface PageContainerProps extends PropsWithChildren {
  /**
   * Layout variant that determines the container's behavior.
   * @default "constrained"
   */
  variant?: PageContainerVariant;
  /**
   * Optional additional CSS class name
   */
  className?: string;
}

/**
 * PageContainer handles all layout concerns (centering, max-width, spacing).
 * This component is self-contained and provides responsive behavior at all breakpoints
 * without relying on parent flex containers.
 *
 * @example
 * ```tsx
 * // Constrained layout (default) - for forms and cards
 * <PageContainer variant="constrained">
 *   <FormContent />
 * </PageContainer>
 *
 * // Centered layout - for full-height centered content
 * <PageContainer variant="centered">
 *   <CenteredContent />
 * </PageContainer>
 *
 * // Full-width layout - for widgets
 * <PageContainer variant="fullWidth">
 *   <CalendlyWidget />
 * </PageContainer>
 * ```
 */
export function PageContainer({
  children,
  variant = "constrained",
  className,
}: PageContainerProps) {
  const variantClass = styles[variant];
  const combinedClassName = className
    ? `${variantClass} ${className}`
    : variantClass;

  return (
    <div className={combinedClassName} data-variant={variant}>
      {children}
    </div>
  );
}
